import React, { useState, useContext, useEffect } from "react";
import {
  Modal,
  Input,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Upload,
  Form,
  message,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import form from "antd/lib/form";
import { RcFile } from "antd/es/upload";
import { UploadOutlined } from "@ant-design/icons";
import { UploadFile } from "antd/lib/upload";
import { AuthContext } from "../../Auth/AuthContext";
import { MdDeleteOutline } from "react-icons/md";
import { addContacts, getAllSources, getAllTags } from "../../utils/API";

const { Option } = Select;
interface ContactsFormValues {
  // LastContacted: any;
  contactName: string;
  email: string;
  phone: string;
  company: string;
  leadStatus: string;
  leadSource: string[];
  tags: string;
  profilePhoto?: File;
}

interface CreateContactModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (values: any) => void | Promise<void>;
  refreshContacts: () => void;
}

const CreateContactModal: React.FC<CreateContactModalProps> = ({
  visible,
  onCancel,
  onCreate,
  refreshContacts,
}) => {
  const [form] = Form.useForm();
  const { authData } = useContext(AuthContext);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const roleId = authData.roleId;
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [sources, setSources] = useState<any[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [tags, setTags] = useState<any[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  type TagType =
    | "Customer"
    | "HotLead"
    | "Influencer"
    | "LostCustomer"
    | "NewLead"
    | "Partner"
    | "Prospect"
    | "Subscriber";

  const tagsStyles: Record<TagType, { background: string; text: string }> = {
    Customer: { background: "#dcfce7", text: "#15803d" },
    HotLead: { background: "#fee2e2", text: "#b91c1c" },
    Influencer: { background: "#dbeafe", text: "#1d4ed8" },
    LostCustomer: { background: "#fef9c3", text: "#ca8a04" },
    NewLead: { background: "#ede9fe", text: "#7c3aed" },
    Partner: { background: "#fce7f3", text: "#db2777" },
    Prospect: { background: "#ffedd5", text: "#ea580c" },
    Subscriber: { background: "#dcfce7", text: "#15803d" },
  };

  // Custom validation function for email or phone
  const validateEmailOrPhone = (_: any, value: any, callback: any) => {
    const { getFieldValue } = form;
    const email = getFieldValue("email");
    const phone = getFieldValue("phone");

    // If both email and phone are empty or only whitespace
    if ((!email || email.trim() === "") && (!phone || phone.trim() === "")) {
      return Promise.reject(
        new Error("Please provide either email or phone number"),
      );
    }
    return Promise.resolve();
  };

  const handleSubmit = async (values: ContactsFormValues) => {
    try {
      setIsLoading(true);

      const formData = new FormData();

      // Append all form values
      Object.entries(values).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Handle arrays (like tags)
          if (Array.isArray(value)) {
            if (value.length > 0) {
              value.forEach((item, index) => {
                if (item) { // Check if item is not null/undefined/empty
                  formData.append(`${key}[${index}]`, item.toString());
                }
              });
            }
          } else if (typeof value === 'object' && value instanceof Date) {
            // Handle Date objects
            formData.append(key, value.toISOString());
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      // Handle LastContacted date properly if it's a string
      // if (values.LastContacted) {
      //   const date = new Date(values.LastContacted);
      //   if (!isNaN(date.getTime())) {
      //     formData.append('LastContacted', date.toISOString());
      //   }
      // }

      // Append profile photo if exists
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append("profilePhoto", fileList[0].originFileObj);
      }

      // Debug: Log form data without for...of
      console.log("Form values:", values);
      console.log("FormData entries:");
      formData.forEach((val, key) => {
        console.log(key, val);
      });

      const response: any = await addContacts(formData);
      console.log("response", response);

      if (response.data.status === true) {
        message.success("contacts added successfully!");
        form.resetFields();
        setFileList([]);
        setPreviewImage("");
        refreshContacts();
        onCancel();
      }
    } catch (error: any) {
      console.error("Error adding lead:", error);
      message.error(
        error.response?.data?.message ||
        error.message ||
        "Something went wrong! Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };
  const getBase64 = (file: File | Blob) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  const imageProps = {
    name: "profilePhoto",
    multiple: false, // Only allow single file upload
    beforeUpload(file: RcFile) {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("You can only upload image files!");
        return Upload.LIST_IGNORE; // Prevent upload
      }
      const isLt64M = file.size / 1024 / 1024 < 64;
      if (!isLt64M) {
        message.error("Image must be smaller than 64MB!");
        return Upload.LIST_IGNORE; // Prevent upload
      }
      return false; // Prevent automatic upload
    },
    onChange(info: any) {
      const newFileList = [...info.fileList];
      if (newFileList.length > 0) {
        const file = newFileList[0];
        if (!file.url && !file.preview) {
          getBase64(file.originFileObj).then((preview) => {
            file.preview = preview;
            setPreviewImage(preview);
          });
        } else {
          setPreviewImage(file.url || file.preview || "");
        }
      } else {
        setPreviewImage("");
      }
      setFileList(newFileList.slice(-1)); // Only keep the last uploaded file
    },
    fileList,
  };

  const fetchSources = async () => {
    try {
      setSourcesLoading(true);

      // ❌ do NOT pass category
      const res = await getAllSources("", 1);

      // ✅ frontend filtering
      const filteredSources = (res.sources || []).filter(
        (source: any) =>
          Array.isArray(source.category) &&
          source.category.includes("Contacts"),
      );

      setSources(filteredSources);
    } catch (err) {
      console.error("Failed to fetch sources", err);
      message.error("Failed to load lead sources");
    } finally {
      setSourcesLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      setTagsLoading(true);

      const res = await getAllTags();
      const allTags = res.tags || res || [];

      // ✅ filter only Contacts tags
      const contactTags = allTags.filter((tag: any) =>
        Array.isArray(tag.category) && tag.category.includes("Contacts")
      );

      setTags(contactTags);
    } catch (err) {
      console.error("Failed to fetch tags", err);
      message.error("Failed to load tags");
    } finally {
      setTagsLoading(false);
    }
  };


  useEffect(() => {
    if (visible) {
      fetchSources();
      fetchTags();
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      centered={false} // Disable centered to use top positioning
      destroyOnClose
      className="custom-modal"
      style={{
        bottom: 20,
        top: 20,
        maxHeight: "calc(100vh - 3  0px)", // Leaves 20px space at bottom
        margin: "0 auto",
        position: "relative",
        // paddingBottom: 20 // Adds space inside modal at bottom
      }}
      bodyStyle={{
        overflowY: "auto",
        // padding: '20px',
        fontFamily: "inter, sans-serif",
      }}
      styles={{
        header: {
          padding: "12px",
          position: "sticky",
          top: 0,
          backgroundColor: "#f1f5ffff",
        },
        content: {
          padding: 0,
          overflow: "hidden",
        },
      }}
      title={
        <div
          style={{
            color: "#4b5563",
            fontFamily: "inter, sans-serif",
            fontSize: "20px",
            fontWeight: 500,
          }}
        >
          Add Contacts
        </div>
      }
    >
      <Form
        onFinish={handleSubmit}
        form={form}
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 19 }}
        colon={false}
        style={{ margin: 0 }}
      >
        <div className="elements">
          <Form.Item
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            className="w-full"
          >
            <div className="flex justify-center ">
              <div className="relative w-20 h-20">
                <Upload.Dragger
                  {...imageProps}
                  className="w-full h-full rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-blue-500 transition-colors bg-gray-50 overflow-hidden"
                  showUploadList={false}
                >
                  {!previewImage ? (
                    <div className="flex flex-col items-center p-1">
                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                        <UploadOutlined className="text-gray-400 text-base" />
                      </div>
                      <span className="text-xs text-gray-400 mt-1">
                        Profile
                      </span>
                    </div>
                  ) : (
                    <img
                      src={previewImage}
                      alt="Uploaded"
                      className="w-full h-full object-cover"
                    />
                  )}
                </Upload.Dragger>

                {previewImage && (
                  <Button
                    type="text"
                    shape="circle"
                    size="small"
                    icon={<MdDeleteOutline size={16} />}
                    className="absolute top-0 right-0 bg-red-500 text-white hover:bg-red-600 shadow-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewImage("");
                      setFileList([]);
                    }}
                  />
                )}
              </div>
            </div>
          </Form.Item>

          {/* Deal Title */}
          <Form.Item
            name="contactName"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Contact Name <span className="text-danger">*</span>
              </span>
            }
            required={false}
            rules={[{ required: true, message: "Please enter contact name" }]}
          >
            <Input
              size="large"
              placeholder="Enter Contact Name"
              className="Allinput"
            />
          </Form.Item>

          {/* Lead Score */}
          <Form.Item
            name="score"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Lead Score
              </span>
            }
            required={false}
          >
            <Input
              size="large"
              type="number"
              placeholder="Enter Lead Score"
              className="Allinput"
            />
          </Form.Item>

          {/* Email */}
          <Form.Item
            name="email"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Email
              </span>
            }
            required={false}
            rules={[
              {
                type: "email",
                message: "Invalid email format",
              },
              // Add validator to check email or phone
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const phone = getFieldValue("phone");
                  if (
                    (!value || value.trim() === "") &&
                    (!phone || phone.trim() === "")
                  ) {
                    return Promise.reject(
                      new Error("Please provide either email or phone number"),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            validateTrigger={["onChange", "onBlur"]}
          >
            <Input
              size="large"
              placeholder="Enter Email"
              className="Allinput"
            />
          </Form.Item>

          {/* Phone Number */}
          <Form.Item
            name="phone"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Phone Number
              </span>
            }
            required={false}
            rules={[
              // Add validator to check email or phone
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const email = getFieldValue("email");
                  if (
                    (!value || value.trim() === "") &&
                    (!email || email.trim() === "")
                  ) {
                    return Promise.reject(
                      new Error("Please provide either email or phone number"),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            validateTrigger={["onChange", "onBlur"]}
          >
            <Input
              size="large"
              placeholder="Enter phone Number"
              className="Allinput"
            />
          </Form.Item>

          {/* Company Name */}
          <Form.Item
            name="company"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Company Name{" "}
              </span>
            }
            required={false}
          >
            <Input
              size="large"
              placeholder="Enter Company Name"
              className="Allinput"
            />
          </Form.Item>

          {/* Lead Source Dropdown */}
          <Form.Item
            name="leadSource"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Lead Source
              </span>
            }
          >
            <Select
              size="large"
              placeholder="Select Lead Source"
              loading={sourcesLoading}
              allowClear
              className="CustomSelect"
              optionLabelProp="label" // Show name in selected value
            >
              {sources.map((source) => (
                <Option key={source._id} value={source._id} label={source.name}>
                  {source.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Date Picker */}
          <Form.Item
            name="LastContacted"
            label={
              <span
                style={{
                  color: "#4b5563",
                  fontFamily: "inter, sans-serif",
                  fontSize: "14px",
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}
              >
                Last Contacted
              </span>
            }
            required={false}
          >
            <DatePicker
              format="DD-MM-YYYY HH:mm"
              size="large"
              showTime
              className="Allinput"
            />
          </Form.Item>

          <Form.Item name="tags" label="Tags">
            <Select
              allowClear
              mode="multiple"
              size="large"
              disabled={roleId === 2}
              placeholder="Select Tags"
              className="CustomSelect"
              loading={tagsLoading}
              optionLabelProp="label" // Show name in selected value
            >
              {tags.map((tag: any) => (
                <Option key={tag._id} value={tag._id} label={tag.name}>
                  <span
                    style={{
                      backgroundColor: tag.color || "#f3f4f6",
                      color: "#111827",
                      padding: "2px 8px",
                      borderRadius: "6px",
                      fontSize: "12px",
                    }}
                  >
                    {tag.name}
                  </span>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        {/* Submit Button */}
        <Row justify="end">
          <Col
            xs={24}
            sm={24}
            md={24}
            xl={24}
            xxl={20}
            className="d-flex justify-content-end me-3"
          >
            <div className="button-wrapper">
              <button
                className="allcanclebutton"
                onClick={onCancel}
                type="button"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="allsubmitebutton"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Adding..." : "Add"}
              </button>
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CreateContactModal;
